/**
 * IXD 셀 선택 → 매칭 DSD 셀로 스크롤 + 나머지 dim 용 대응 집합.
 *
 * 선택 상태·매칭 결과·스크롤은 하나의 상태 기계다 — "IXD 에서 무엇을 골랐나" 하나가
 * 원인이고 "어디로 스크롤" 과 "어느 DSD 를 강조" 는 같은 원인의 두 결과다. 셋을 나눠
 * 두면 라이브(`compare-content-view`)와 다운로드 뷰어(`export/viewer`)가 배선을 각자
 * 다시 짜게 되고, 실제로 선택 리셋이 한쪽에만 생겨 뷰어에는 청크를 넘겨도 이전 선택이
 * 남아 있었다. 그래서 훅이 셋 다 소유한다.
 *
 * 반대 방향은 `useDsdIxdCounterpart` 가 같은 모양으로 담당한다.
 *
 * 매칭이 하나도 없으면 이전 포커스 링을 푼다. 안 그러면 무관한 셀의 강조만 남고
 * 화면은 따라가지 않아, 그 셀이 이번 선택의 대응인 것처럼 보인다.
 */

import { useCallback, useMemo, useState } from "react";
import type { DSDSubChunkInfo } from "@/apis/compare/route";
import type { PreviewPanelHandle } from "@/components/ixd-dsd-compare/preview-panel";
import { normalizeCellId } from "@/utils/ixd-cell-model";
import { buildDsdByIxdCellIdMap } from "@/utils/ixd-dsd-compare/build-dsd-by-ixd-cell-id";

interface SelectedIxdCell {
  cellId: string;
  /** 동일 cellId 로 충돌하는 다른 축 셀과 안 섞이도록 identity 우선 조회에 쓴다. */
  identityKey: string | null;
  /** 고른 셀이 속한 DL. 같은 fact 를 두 DL 이 태깅했을 때 목적지를 가른다. */
  dlViewId: string | null;
}

interface Params {
  subChunks: DSDSubChunkInfo[] | undefined | null;
  previewPanelRef: React.RefObject<PreviewPanelHandle | null>;
  /** 값이 바뀌면 선택을 비운다(청크 전환·편집 모드 토글 등 stale 선택이 남는 자리). */
  resetKey?: string | null;
  /** 편집 모드 등 이 동선 전체(스크롤·강조)를 끄고 싶을 때. */
  disabled?: boolean;
}

/**
 * IXD 셀 선택을 받아 대응 DSD 셀로 스크롤하고, 그 대응 집합을 돌려준다.
 *
 * 반대 방향 `useDsdIxdCounterpart` 와 대칭이지만 **네 군데가 다르다.** 한쪽을 고칠 때
 * 다른 쪽에 그대로 옮기면 안 되는 지점들이다.
 *
 * 1. **단일 선택도 받는다.** IXD 는 클릭 하나에도 대응 DSD 를 보여준다. (DSD 쪽은
 *    드래그 2개 이상만 — 단일 클릭은 `useNavigation` 이 소유한다.)
 * 2. **조회가 두 단계다.** 한 IXD 셀이 identity 키와 정규화 cellId 두 가지로 등록될 수
 *    있어 identity 우선 → cellId 폴백이 필요하다. (DSD 쪽 키인 dsdId 는 유일하다.)
 * 3. **스크롤을 직접 하지 않는다.** `PreviewPanelHandle` 에 위임한다 — 스크롤 기준선과
 *    포커스 링은 DSD 렌더러가 소유해야 클릭 네비게이션과 상태가 안 섞인다.
 * 4. **매칭이 없으면 `clearFocus()` 로 링을 푼다.** DSD 쪽엔 이 동작이 없다(다중 선택
 *    에서는 링이 애초에 꺼져 있다).
 *
 * @param subChunks - 현재 DSD 청크. 여기서 역방향 매칭 맵을 만든다.
 * @param previewPanelRef - 스크롤·포커스 해제를 위임할 DSD 패널 핸들.
 * @param resetKey - 값이 바뀌면 선택을 비운다(청크 전환·편집 토글).
 * @param disabled - 스크롤과 강조를 함께 끈다(편집 모드).
 * @returns `onIxdCellIdsChange` 를 IXD 렌더러의 `onSelectedCellIdsChange` 에,
 *   `counterpartDsdIds` 를 DSD 패널의 같은 이름 prop 에 그대로 연결한다.
 */
export const useIxdDsdCounterpart = ({
  subChunks,
  previewPanelRef,
  resetKey,
  disabled,
}: Params) => {
  const dsdByIxdCellId = useMemo(
    () => buildDsdByIxdCellIdMap(subChunks),
    [subChunks],
  );

  const [selectedCells, setSelectedCells] = useState<SelectedIxdCell[]>([]);
  // resetKey 가 바뀐 렌더에서 곧바로 비운다 — 이펙트로 미루면 그 프레임의 대응 집합이
  // 이전 선택 × 새 청크 맵으로 계산돼 빈 set 이 되고, 전부 dim 된 화면이 한 번 보인다.
  const [prevResetKey, setPrevResetKey] = useState(resetKey);
  if (prevResetKey !== resetKey) {
    setPrevResetKey(resetKey);
    setSelectedCells([]);
  }

  /** identity 로 먼저 찾고, 등록이 없으면 구버전 레코드용 cellId 로 폴백. */
  const findMatchedDsd = useCallback(
    (cellId: string, identityKey: string | null) =>
      (identityKey ? dsdByIxdCellId.get(identityKey) : undefined) ??
      dsdByIxdCellId.get(normalizeCellId(cellId)),
    [dsdByIxdCellId],
  );

  const onIxdCellIdsChange = useCallback(
    (
      cellIds: string[],
      identityKeys?: Array<string | null>,
      dlViewIds?: Array<string | null>,
    ) => {
      const cells = cellIds.map((cellId, index) => ({
        cellId,
        identityKey: identityKeys?.[index] ?? null,
        dlViewId: dlViewIds?.[index] ?? null,
      }));
      setSelectedCells(cells);

      if (disabled || cells.length === 0) return;
      for (const { cellId, identityKey, dlViewId } of cells) {
        const matched = findMatchedDsd(cellId, identityKey);
        if (!matched) continue;
        // 정본 지목은 백엔드가 실어준 DL 별로 나뉘어 있다 — 같은 fact 를 두 DL 이
        // 태깅하면 양쪽 DSD 셀이 같은 cellId 를 지목하기 때문이다. 고른 IXD 셀의
        // DL 로 그중 하나를 집고, 그 DL 의 지목이 없으면 DL 을 모르는 지목으로,
        // 그것도 없으면 매칭 순서로 물러선다.
        const properByDl = matched.properDsdIdByDlViewId;
        const targetDsdId =
          properByDl.get(dlViewId) ??
          properByDl.get(null) ??
          matched.dsdIds.values().next().value;
        if (targetDsdId) {
          previewPanelRef.current?.scrollToDsdId(targetDsdId);
          return;
        }
      }
      previewPanelRef.current?.clearFocus();
    },
    [disabled, findMatchedDsd, previewPanelRef],
  );

  /**
   * 포커스 대상 dsdId 집합. **세 상태를 구분해야 해서** 배열이 아니라 `Set | null` 이다.
   *
   * - `null` — 이 동선이 꺼짐. DSD 는 평소 표시를 유지한다.
   * - 비어 있지 않은 set — 그 셀만 강조하고 **나머지는 dim**.
   * - 빈 set — "매칭이 하나도 없다". 전부 dim 된다.
   *
   * 뒤 두 상태가 화면에서 전혀 다르므로 선택 없음을 빈 set 으로 내리면 아무 선택도 안
   * 했는데 표 전체가 어두워진다. 반대 방향(`useDsdIxdCounterpart`)은 외곽선만 그려
   * dim 을 만들지 않으므로 빈 배열 하나로 충분하다.
   */
  const counterpartDsdIds = useMemo<Set<string> | null>(() => {
    if (disabled || selectedCells.length === 0) return null;
    const ids = new Set<string>();
    for (const { cellId, identityKey } of selectedCells) {
      findMatchedDsd(cellId, identityKey)?.dsdIds.forEach((dsdId) =>
        ids.add(dsdId),
      );
    }
    // 강조(dim)는 DL 로 안 좁힌다 — 다른 DL 의 매칭도 실제 대응이라 어둡게 만들면
    // 매칭이 없는 것처럼 보인다. 좁히는 건 스크롤 목적지 하나뿐이다.
    return ids;
  }, [disabled, selectedCells, findMatchedDsd]);

  return { onIxdCellIdsChange, counterpartDsdIds };
};
